import React, { useMemo } from 'react'

import {
  Box,
  Button,
  HStack,
  IconButton,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { Link, generatePath } from 'react-router'
// eslint-disable-next-line no-restricted-imports -- 新しいアイコンに移行する
import {
  Add,
  Seek,
  PaginationTop,
  PaginationBack,
  PaginationNext,
  PaginationEnd,
} from 'src/components/__legacy__/icon/monochrome'
import { Paths } from 'src/constants/paths'
import {
  useCurrentMember,
  usePermission,
  useSelectedTrial,
} from 'src/features/auth/context'
import { PERMISSIONS } from 'src/lib/permission'

import { useExplanationPagination } from '../../hooks/useExplanationPagination'
import { ExplanationFilterDrawer } from '../ExplanationFilterDrawer/ExplanationFilterDrawer'
import { ExplanationsTable } from '../ExplanationsTable/ExplanationsTable'

export const ExplanationList = () => {
  const { selectedTrial } = useSelectedTrial()

  const { selectedTrialHospitalRole } = useCurrentMember()

  const { hasPermission } = usePermission()

  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onOpenFilterDrawer,
    onClose: onCloseFilterDrawer,
  } = useDisclosure()

  const canAddExplanation = useMemo(() => {
    if (!hasPermission(PERMISSIONS.Explanation_Edit)) {
      return false
    }
    // 遠隔プレスク対応以降は実施医療機関のユーザーのみ同意説明を登録できる
    if (selectedTrialHospitalRole !== 'Main') {
      return false
    }
    return true
  }, [hasPermission, selectedTrialHospitalRole])

  return (
    <Stack h="full" spacing="1.5rem" direction="column" p="6">
      <Stack spacing="0" direction="row" alignItems="center">
        <Text as="h1" fontWeight="bold" fontSize="xl">
          同意説明一覧
        </Text>
        <Spacer />
        <HStack spacing="4">
          {canAddExplanation && (
            <Button
              variant="outline"
              leftIcon={<Add />}
              as={Link}
              to={generatePath(Paths.ExplanationsNew, {
                trialUid: selectedTrial.uid,
              })}
            >
              新規作成
            </Button>
          )}
          <Button
            variant="text"
            leftIcon={<Seek />}
            onClick={onOpenFilterDrawer}
          >
            検索/絞り込み
          </Button>
        </HStack>
      </Stack>
      <Box display="flex" justifyContent="flex-end">
        <ExplanationPagination />
      </Box>
      <Box flex="1" overflow="auto">
        <ExplanationsTable />
      </Box>
      <ExplanationFilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={onCloseFilterDrawer}
      />
    </Stack>
  )
}

const ExplanationPagination = () => {
  const {
    rangeString,
    totalCount,
    hasFirstPage,
    hasPrevPage,
    hasNextPage,
    hasLastPage,
    handleClickFirstPage,
    handleClickPrevPage,
    handleClickNextPage,
    handleClickLastPage,
  } = useExplanationPagination()

  return (
    <Stack direction="row" spacing="0.25rem">
      <IconButton
        variant="ghost"
        height="1.5rem"
        minWidth="1.5rem"
        borderRadius="sm"
        aria-label="pagination top button"
        icon={<PaginationTop />}
        onClick={handleClickFirstPage}
        disabled={!hasFirstPage}
      />
      <IconButton
        variant="ghost"
        height="1.5rem"
        minWidth="1.5rem"
        borderRadius="sm"
        aria-label="pagination back button"
        icon={<PaginationBack />}
        onClick={handleClickPrevPage}
        disabled={!hasPrevPage}
      />
      <Text fontSize="sm">{rangeString}</Text>
      <Text fontSize="sm">/</Text>
      <Text fontSize="sm">{`${totalCount} 件`}</Text>
      <IconButton
        variant="ghost"
        height="1.5rem"
        minWidth="1.5rem"
        borderRadius="sm"
        aria-label="pagination next button"
        icon={<PaginationNext />}
        onClick={handleClickNextPage}
        disabled={!hasNextPage}
      />
      <IconButton
        variant="ghost"
        height="1.5rem"
        minWidth="1.5rem"
        borderRadius="sm"
        aria-label="pagination end button"
        icon={<PaginationEnd />}
        onClick={handleClickLastPage}
        disabled={!hasLastPage}
      />
    </Stack>
  )
}
